import React, { useState, useEffect, useMemo, useCallback } from "react";
import { Box, Stack, IconButton, Tooltip } from "@mui/material";

import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutlineOutlined";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutlineOutlined";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import ArchiveIcon from "@mui/icons-material/Archive";
import UnarchiveIcon from "@mui/icons-material/Unarchive";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import SyncIcon from "@mui/icons-material/Sync";

import PageContainer from "../layouts/PageContainer";
import PageToolbar from "../components/common/PageToolbar";
import AppButton from "../components/buttons/AppButton";
import StandardTable, { TableColumn } from "../components/common/StandardTable";
import ConfirmDialog from "../components/common/ConfirmDialog";

import CreateItemModal from "../components/items/CreateItemModal";
import UpdateItemModal from "../components/items/UpdateItemModal";
import ViewItemModal from "../components/items/ViewItemModal";
import ImportItemsModal from "../components/items/ImportItemsModal";
import SyncItemsModal from "../components/items/SyncItemsModal";

import useDebounce from "../hooks/useDebounce";
import { usePermissions } from "../hooks/usePermissions";
import { useAppDispatch, useAppSelector } from "../redux/store";
import {
  getItems,
  getItemById,
  deleteItem,
  archiveItem,
  importItems,
  syncItemsToBranch,
} from "../redux/actions/itemActions";
import { itemActions } from "../redux/slices/item";
import StatusChip from "../components/common/StatusChip";
import { formatPlainNumber } from "../utils/appUtils";
import { ItemResponseDto } from "../shared/dtos/item/ItemResponseDto";

const ItemsPage: React.FC = () => {
  const dispatch = useAppDispatch();
  const { hasPermission } = usePermissions();

  const {
    items,
    pagination,
    loadingFetchAll: loadingItems,
    errorFetchAll: errorLoadingItems,
    serverStatus,
    loadingCreate: loadingItemCreate,
    errorCreate: errorCreatingItem,
    loadingUpdate: loadingItemUpdate,
    errorUpdate: errorUpdatingItem,
    loadingDelete: loadingItemDelete,
    loadingArchive: loadingItemArchive,
    loadingImport: loadingItemImport,
    errorImport: errorItemImport,
    importReport: itemImportReport,
    loadingSync: loadingItemSync,
    errorSync: errorItemSync,
    syncReport: itemSyncReport,
    item,
    loadingFetchOne: loadingItem,
    errorFetchOne: errorLoadingItem,
  } = useAppSelector((state) => state.item);

  const [searchTerm, setSearchTerm] = useState<string>("");
  const debouncedSearch = useDebounce<string>(searchTerm, 500);
  const [pageConfig, setPageConfig] = useState({ page: 1, limit: 10 });

  const [isCreateModalOpen, setIsCreateModalOpen] = useState<boolean>(false);
  const [isUpdateModalOpen, setIsUpdateModalOpen] = useState<boolean>(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState<boolean>(false);
  const [isArchiveModalOpen, setIsArchiveModalOpen] = useState<boolean>(false);
  const [isViewModalOpen, setIsViewModalOpen] = useState<boolean>(false);
  const [isImportModalOpen, setIsImportModalOpen] = useState<boolean>(false);
  const [isSyncModalOpen, setIsSyncModalOpen] = useState<boolean>(false);

  const [itemForOperation, setItemForOperation] =
    useState<ItemResponseDto | null>(null);

  const canCreate = hasPermission("item:create");
  const canUpdate = hasPermission("item:update");
  const canDelete = hasPermission("item:delete");
  const canView = hasPermission("item:read");
  const canImport = hasPermission("item:create");

  const fetchItemsData = useCallback(() => {
    dispatch(
      getItems({
        page: pageConfig.page,
        size: pageConfig.limit,
        search: debouncedSearch,
      }),
    );
  }, [dispatch, pageConfig.page, pageConfig.limit, debouncedSearch]);

  useEffect(() => {
    fetchItemsData();
  }, [fetchItemsData]);

  useEffect(() => {
    setPageConfig((prev) => ({ ...prev, page: 1 }));
  }, [debouncedSearch]);

  const handleOpenCreate = (): void => setIsCreateModalOpen(true);
  const handleCloseCreate = useCallback(() => {
    if (!loadingItemCreate) setIsCreateModalOpen(false);
  }, [loadingItemCreate]);

  const handleOpenUpdate = (row: ItemResponseDto): void => {
    setItemForOperation(row);
    setIsUpdateModalOpen(true);
  };
  const handleCloseUpdate = useCallback(() => {
    if (!loadingItemUpdate) {
      setIsUpdateModalOpen(false);
      setItemForOperation(null);
    }
  }, [loadingItemUpdate]);

  const handleOpenDelete = (row: ItemResponseDto): void => {
    setItemForOperation(row);
    setIsDeleteModalOpen(true);
  };
  const handleCloseDelete = useCallback(() => {
    if (!loadingItemDelete) {
      setIsDeleteModalOpen(false);
      setItemForOperation(null);
    }
  }, [loadingItemDelete]);

  const handleOpenArchive = (row: ItemResponseDto): void => {
    setItemForOperation(row);
    setIsArchiveModalOpen(true);
  };
  const handleCloseArchive = useCallback(() => {
    if (!loadingItemArchive) {
      setIsArchiveModalOpen(false);
      setItemForOperation(null);
    }
  }, [loadingItemArchive]);

  // --- Confirm Handlers ---
  const handleDeleteConfirm = (): void => {
    if (itemForOperation) {
      dispatch(deleteItem(itemForOperation.id));
    }
  };

  const handleArchiveConfirm = (): void => {
    if (itemForOperation) {
      dispatch(archiveItem(itemForOperation.id));
    }
  };

  const handleOpenView = (row: ItemResponseDto): void => {
    dispatch(getItemById(row.id));
    setIsViewModalOpen(true);
  };
  const handleCloseView = (): void => setIsViewModalOpen(false);

  const handleOpenImport = (): void => {
    dispatch(itemActions.resetReports());
    setIsImportModalOpen(true);
  };
  const handleCloseImport = (): void => {
    if (!loadingItemImport) {
      setIsImportModalOpen(false);
      fetchItemsData();
    }
  };

  const handleExecuteImport = (file: File): void => {
    dispatch(importItems(file));
  };

  // --- Sync Handlers ---
  const handleOpenSync = (): void => {
    dispatch(itemActions.resetReports());
    setIsSyncModalOpen(true);
  };

  const handleCloseSync = (): void => {
    if (!loadingItemSync) {
      setIsSyncModalOpen(false);
      if (itemSyncReport) {
        fetchItemsData();
      }
    }
  };

  const handleExecuteSync = (): void => {
    dispatch(syncItemsToBranch());
  };

  useEffect(() => {
    if (!serverStatus) return;
    const { operation, status } = serverStatus;
    if (operation === "create" && (status === 201 || status === 200))
      handleCloseCreate();
    if (operation === "update" && status === 200) handleCloseUpdate();
    if (operation === "delete" && status === 200) handleCloseDelete();
    if (operation === "archive" && status === 200) handleCloseArchive();
    dispatch(itemActions.setServerStatus(null));
  }, [
    serverStatus,
    dispatch,
    handleCloseCreate,
    handleCloseUpdate,
    handleCloseDelete,
    handleCloseArchive,
  ]);

  const columns: TableColumn<ItemResponseDto>[] = useMemo(
    () => [
      {
        id: "name",
        label: "Item Name",
        minWidth: 180,
        format: (_, row) => (
          <Box component="span" sx={{ fontWeight: 600 }}>
            {row.name}
          </Box>
        ),
      },
      { id: "sku", label: "SKU", minWidth: 100 },
      {
        id: "brandName",
        label: "Brand",
        minWidth: 120,
      },
      { id: "categoryName", label: "Category", minWidth: 120 },
      {
        id: "retailPrice",
        label: "Retail",
        minWidth: 100,
        align: "right",
        format: (_, row) => formatPlainNumber(row.retailPrice),
      },
      {
        id: "wholesalePrice",
        label: "Wholesale",
        minWidth: 100,
        align: "right",
        format: (_, row) => formatPlainNumber(row.wholesalePrice),
      },
      { id: "itemType", label: "Type", minWidth: 100 },
      {
        id: "archived",
        label: "Status",
        minWidth: 100,
        format: (_, row) => (
          <StatusChip status={row.archived ? "ARCHIVED" : "ACTIVE"} />
        ),
      },

      // --- HIDDEN BY DEFAULT ---
      {
        id: "buyingPrice",
        label: "Cost",
        minWidth: 100,
        align: "right",
        defaultHidden: true,
        format: (_, row) => formatPlainNumber(row.buyingPrice),
      },
      {
        id: "taxCodeName",
        label: "Tax Code",
        minWidth: 120,
        defaultHidden: true,
      },
      {
        id: "packageUnitName",
        label: "Package Unit",
        minWidth: 100,
        defaultHidden: true,
      },
      {
        id: "quantityUnitName",
        label: "Qty Unit",
        minWidth: 100,
        defaultHidden: true,
      },
      {
        id: "lowStockThreshold",
        label: "Low Stock Alert",
        minWidth: 120,
        align: "center",
        defaultHidden: true,
      },
    ],
    [],
  );

  const renderRowActions = (row: ItemResponseDto): React.JSX.Element => (
    <Stack direction="row" spacing={1} sx={{ justifyContent: "center" }}>
      {canView && (
        <Tooltip title="View">
          <IconButton
            size="small"
            color="info"
            onClick={() => handleOpenView(row)}
          >
            <VisibilityOutlinedIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      )}
      {canUpdate && (
        <>
          <Tooltip title="Edit">
            <IconButton
              size="small"
              color="secondary"
              onClick={() => handleOpenUpdate(row)}
            >
              <EditOutlinedIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title={row.archived ? "Unarchive" : "Archive"}>
            <IconButton
              size="small"
              color="warning"
              onClick={() => handleOpenArchive(row)}
            >
              {row.archived ? (
                <UnarchiveIcon fontSize="small" />
              ) : (
                <ArchiveIcon fontSize="small" />
              )}
            </IconButton>
          </Tooltip>
        </>
      )}
      {canDelete && (
        <Tooltip title="Delete">
          <IconButton
            size="small"
            color="error"
            onClick={() => handleOpenDelete(row)}
          >
            <DeleteOutlineIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      )}
    </Stack>
  );

  return (
    <PageContainer
      title="Items & Services"
      subtitle="Manage your inventory catalog, pricing, and classifications."
      actions={
        <Stack direction="row" spacing={2}>
          {canUpdate && (
            <AppButton
              variant="contained"
              color="warning"
              startIcon={<SyncIcon />}
              onClick={handleOpenSync}
            >
              Sync
            </AppButton>
          )}
          {canImport && (
            <AppButton
              variant="outlined"
              color="secondary"
              startIcon={<CloudUploadIcon />}
              onClick={handleOpenImport}
            >
              Import
            </AppButton>
          )}
          {canCreate && (
            <AppButton
              variant="contained"
              startIcon={<AddCircleOutlineIcon />}
              onClick={handleOpenCreate}
            >
              Add Item
            </AppButton>
          )}
        </Stack>
      }
    >
      <PageToolbar
        searchTerm={searchTerm}
        onSearchChange={(e) => setSearchTerm(e.target.value)}
        searchPlaceholder="Search by name or SKU..."
      />

      <StandardTable
        columns={columns}
        data={items}
        loading={loadingItems}
        error={errorLoadingItems}
        onRetry={fetchItemsData}
        page={pageConfig.page}
        count={pagination?.totalElements || 0}
        rowsPerPage={pageConfig.limit}
        onPageChange={(p) => setPageConfig((c) => ({ ...c, page: p }))}
        onRowsPerPageChange={(l) =>
          setPageConfig((c) => ({ ...c, limit: l, page: 1 }))
        }
        enableColumnManagement={true}
        rowAction={renderRowActions}
      />

      {isCreateModalOpen && (
        <CreateItemModal
          open={isCreateModalOpen}
          onClose={handleCloseCreate}
          loading={loadingItemCreate}
          error={errorCreatingItem}
        />
      )}
      {isUpdateModalOpen && itemForOperation && (
        <UpdateItemModal
          open={isUpdateModalOpen}
          onClose={handleCloseUpdate}
          item={itemForOperation}
          loading={loadingItemUpdate}
          error={errorUpdatingItem}
        />
      )}
      {isViewModalOpen && (
        <ViewItemModal
          open={isViewModalOpen}
          onClose={handleCloseView}
          item={item}
          loading={loadingItem}
          error={errorLoadingItem}
        />
      )}
      {isImportModalOpen && (
        <ImportItemsModal
          open={isImportModalOpen}
          onClose={handleCloseImport}
          onImport={handleExecuteImport}
          loading={loadingItemImport}
          error={errorItemImport}
          report={itemImportReport}
        />
      )}
      {isSyncModalOpen && (
        <SyncItemsModal
          open={isSyncModalOpen}
          onClose={handleCloseSync}
          onSync={handleExecuteSync}
          loading={loadingItemSync}
          error={errorItemSync}
          report={itemSyncReport}
        />
      )}
      {isDeleteModalOpen && itemForOperation && (
        <ConfirmDialog
          open={isDeleteModalOpen}
          onClose={handleCloseDelete}
          onConfirm={handleDeleteConfirm}
          title="Delete Item"
          description={`Are you sure you want to delete "${itemForOperation.name}"?`}
          confirmText="Delete"
          severity="error"
          loading={loadingItemDelete}
        />
      )}
      {isArchiveModalOpen && itemForOperation && (
        <ConfirmDialog
          open={isArchiveModalOpen}
          onClose={handleCloseArchive}
          onConfirm={handleArchiveConfirm}
          title={itemForOperation.archived ? "Unarchive" : "Archive"}
          description={`Are you sure you want to ${itemForOperation.archived ? "unarchive" : "archive"} "${itemForOperation.name}"?`}
          confirmText={itemForOperation.archived ? "Unarchive" : "Archive"}
          severity="warning"
          loading={loadingItemArchive}
        />
      )}
    </PageContainer>
  );
};

export default ItemsPage;
